import { Controller } from "@hotwired/stimulus";

/**
 * Before/After image comparison slider.
 *
 * Reveals the "after" image on top of the "before" image through a draggable
 * handle. Works with mouse, touch and pen (unified Pointer Events) and is fully
 * keyboard accessible (the handle exposes the WAI-ARIA `slider` pattern).
 *
 * Connects to `data-controller="compare"`.
 *
 * Targets:
 *   - after  : the overlaid image whose `clip-path` is updated (required)
 *   - handle : the draggable divider exposing `role="slider"` (required)
 *
 * Values:
 *   - position : current divider position, as a percentage (0–100, default 50)
 *   - step     : keyboard increment for arrow keys, in percent (default 2)
 *   - pageStep : keyboard increment for PageUp/PageDown, in percent (default 10)
 *
 * Expected markup (see `Shared::CompareSlider` for the canonical version):
 *
 *   <div data-controller="compare"
 *        data-action="pointerdown->compare#startDrag
 *                     pointermove->compare#drag
 *                     pointerup->compare#endDrag
 *                     pointercancel->compare#endDrag">
 *     <img src="before.jpg" alt="…">
 *     <img src="after.jpg" alt="…" data-compare-target="after">
 *     <div role="slider" tabindex="0" data-compare-target="handle"
 *          data-action="keydown->compare#nudge"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["after", "handle"];
  static values = {
    position: { type: Number, default: 50 },
    step: { type: Number, default: 2 },
    pageStep: { type: Number, default: 10 },
  };

  connect() {
    this._render();
  }

  // Pointer capture keeps the drag alive even when the cursor leaves the
  // element, so no document-level listeners (and their cleanup) are needed.
  startDrag(event) {
    event.preventDefault();
    this._dragging = true;
    this.element.setPointerCapture(event.pointerId);
    this.handleTarget.focus();
    this._moveToPointer(event);
  }

  drag(event) {
    if (!this._dragging) return;
    this._moveToPointer(event);
  }

  endDrag(event) {
    if (!this._dragging) return;
    this._dragging = false;
    if (this.element.hasPointerCapture(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId);
    }
  }

  // Keyboard control, following the WAI-ARIA slider pattern.
  nudge(event) {
    const offsets = {
      ArrowLeft: -this.stepValue,
      ArrowDown: -this.stepValue,
      ArrowRight: this.stepValue,
      ArrowUp: this.stepValue,
      PageDown: -this.pageStepValue,
      PageUp: this.pageStepValue,
    };

    let next;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = 100;
    else if (event.key in offsets) next = this.positionValue + offsets[event.key];
    else return;

    event.preventDefault();
    this._setPosition(next);
  }

  positionValueChanged() {
    this._render();
  }

  _moveToPointer(event) {
    const rect = this.element.getBoundingClientRect();
    if (rect.width === 0) return;
    this._setPosition(((event.clientX - rect.left) / rect.width) * 100);
  }

  _setPosition(percentage) {
    this.positionValue = Math.min(100, Math.max(0, percentage));
  }

  _render() {
    if (!this.hasAfterTarget || !this.hasHandleTarget) return;

    const position = this.positionValue;
    // Reveal the "after" image from the divider to the right edge.
    this.afterTarget.style.clipPath = `inset(0 0 0 ${position}%)`;
    this.handleTarget.style.left = `${position}%`;
    this.handleTarget.setAttribute("aria-valuenow", Math.round(position));
  }
}
